import React from 'react';
import { X, AlertTriangle, CheckCircle, Info } from 'lucide-react';

interface ValidationIssue {
  product_id: number;
  product_name: string;
  reason: string;
  message: string;
  requested_quantity?: number;
  available_quantity?: number;
}

interface CartValidationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  itemsToRemove: Array<{
    product_id: number;
    product_name: string;
    reason: string;
    message: string;
  }>;
  itemsToAdjust: Array<{
    product_id: number;
    product_name: string;
    reason: string;
    message: string;
    requested_quantity: number;
    available_quantity: number;
  }>;
}

const CartValidationModal: React.FC<CartValidationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  itemsToRemove,
  itemsToAdjust
}) => {
  if (!isOpen) return null;

  const hasIssues = itemsToRemove.length > 0 || itemsToAdjust.length > 0;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-cosmic-card rounded-2xl shadow-2xl max-w-md w-full mx-4 transform transition-all duration-300 scale-100">
        {/* Header */}
        <div className="relative p-6 pb-4">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          
          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-white" />
            </div>
          </div>
          
          {/* Title */}
          <h2 className="text-2xl font-bold text-white text-center mb-2">
            Cart Update Required
          </h2>
          
          <p className="text-gray-300 text-center text-sm">
            Some items in your cart are no longer available or have limited stock.
          </p>
        </div>

        {/* Content */}
        <div className="px-6 pb-6">
          {itemsToRemove.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-red-400 mb-3 flex items-center gap-2">
                <X className="w-5 h-5" />
                Items Removed (Out of Stock)
              </h3>
              <div className="space-y-2">
                {itemsToRemove.map((item) => (
                  <div key={item.product_id} className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                    <p className="text-red-300 text-sm font-medium">{item.product_name}</p>
                    <p className="text-red-400/70 text-xs mt-1">{item.message}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {itemsToAdjust.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-yellow-400 mb-3 flex items-center gap-2">
                <Info className="w-5 h-5" />
                Quantities Adjusted
              </h3>
              <div className="space-y-2">
                {itemsToAdjust.map((item) => (
                  <div key={item.product_id} className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                    <p className="text-yellow-300 text-sm font-medium">{item.product_name}</p>
                    <p className="text-yellow-400/70 text-xs mt-1">
                      Quantity reduced from {item.requested_quantity} to {item.available_quantity}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col gap-3">
            <button
              onClick={onConfirm}
              className="w-full bg-gradient-to-r from-primary to-secondary text-white font-bold py-3 px-6 rounded-xl hover:opacity-90 transition-opacity shadow-lg"
            >
              Update Cart & Continue
            </button>
            <button
              onClick={onClose}
              className="w-full bg-gray-800 text-gray-300 font-semibold py-3 px-6 rounded-xl hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartValidationModal;
